import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, CalendarDays, Inbox, LogOut, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem("psl_token");

const inputCls =
  "w-full rounded-md border border-ink/20 bg-cream px-4 py-2.5 text-sm text-ink placeholder:text-slate2/60 outline-none transition-colors duration-200 focus:border-plum focus:ring-2 focus:ring-plum/15";

async function apiFetch(path, { method = "GET", body } = {}) {
  const res = await fetch(`${API}${path}`, {
    method,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401) {
    localStorage.removeItem("psl_token");
    window.location.reload();
    return null;
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail = data.detail;
    throw new Error(typeof detail === "string" ? detail : "Something went wrong");
  }
  return data;
}

const Field = ({ label, children }) => (
  <div>
    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.15em] text-ink/70">{label}</label>
    {children}
  </div>
);

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState("posts");

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!getToken()) {
      setUser(null);
      return;
    }
    apiFetch("/auth/me")
      .then(setUser)
      .catch(() => setUser(null));
  }, []);

  if (user === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream">
        <p className="font-serif text-xl italic text-slate2">Checking the register…</p>
      </div>
    );
  }

  if (!user) return <Login onSuccess={setUser} />;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-cream">
      <header className="border-b border-ink/10 bg-cream/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1200px] items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <img src="/images/logo-books.png" alt="Purple Space Library" className="h-9 w-9 rounded-lg object-cover ring-1 ring-ink/10" />
            <div>
              <p className="font-serif text-lg leading-none text-ink">
                Library <span className="italic text-plum">Desk</span>
              </p>
              <p className="text-xs text-slate2">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="admin-view-site" className="text-sm font-medium text-slate2 transition-colors duration-200 hover:text-plum">
              View site
            </Link>
            <button
              data-testid="admin-logout"
              onClick={() => {
                localStorage.removeItem("psl_token");
                setUser(null);
              }}
              className="inline-flex items-center gap-2 rounded-full border border-ink/25 px-5 py-2 text-sm font-semibold text-ink transition-colors duration-200 hover:border-plum hover:text-plum"
            >
              <LogOut size={14} /> Log out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1200px] px-6 py-10">
        <div className="flex flex-wrap gap-2">
          {[
            ["posts", "Journal Posts", BookOpen],
            ["events", "Event Calendar", CalendarDays],
            ["visits", "Visit Enquiries", Inbox],
          ].map(([id, label, Icon]) => (
            <button
              key={id}
              data-testid={`admin-tab-${id}`}
              onClick={() => setTab(id)}
              className={`inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold transition-colors duration-200 ${
                tab === id ? "bg-plum text-cream" : "border border-ink/20 text-ink hover:border-plum hover:text-plum"
              }`}
            >
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>

        <div className="mt-10">
          {tab === "posts" && <PostsTab />}
          {tab === "events" && <EventsTab />}
          {tab === "visits" && <VisitsTab />}
        </div>
      </main>
    </div>
  );
}

function Login({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Login failed");
      localStorage.setItem("psl_token", data.token);
      onSuccess(data.user);
      toast.success("Welcome back to the library desk.");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-cream px-6">
      <form
        data-testid="admin-login-form"
        onSubmit={submit}
        className="w-full max-w-md rounded-3xl border border-ink/10 bg-cream p-10 shadow-[0_40px_80px_-50px_rgba(93,46,70,0.4)]"
      >
        <img src="/images/logo-books.png" alt="Purple Space Library" className="h-14 w-14 rounded-xl object-cover ring-1 ring-ink/10" />
        <h1 className="mt-6 font-serif text-3xl text-ink">The Library Desk</h1>
        <p className="mt-2 text-sm text-slate2">Sign in to post reviews and update the calendar.</p>

        <div className="mt-8 space-y-5">
          <Field label="Email">
            <input data-testid="admin-login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@purplespacelibrary.in" className={inputCls} />
          </Field>
          <Field label="Password">
            <input data-testid="admin-login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={inputCls} />
          </Field>
        </div>

        {error && (
          <p data-testid="admin-login-error" className="mt-4 rounded-lg bg-terracotta/15 px-4 py-3 text-sm font-medium text-plum">
            {error}
          </p>
        )}

        <button
          type="submit"
          data-testid="admin-login-submit"
          disabled={loading}
          className="mt-8 w-full rounded-full bg-plum px-6 py-3.5 text-sm font-semibold text-cream transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60"
        >
          {loading ? "Opening the desk…" : "Sign in"}
        </button>

        <Link to="/" className="mt-6 block text-center text-sm font-medium text-slate2 transition-colors duration-200 hover:text-plum">
          Back to the website
        </Link>
      </form>
    </div>
  );
}

function PostsTab() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => apiFetch("/posts").then((d) => d && setPosts(d));
  useEffect(() => {
    load();
  }, []);

  const save = async (form) => {
    if (form.id) {
      await apiFetch(`/posts/${form.id}`, { method: "PUT", body: form });
      toast.success("Article updated.");
    } else {
      await apiFetch("/posts", { method: "POST", body: form });
      toast.success("Article published to the journal.");
    }
    setEditing(null);
    load();
  };

  const remove = async (id) => {
    await apiFetch(`/posts/${id}`, { method: "DELETE" });
    toast.success("Article removed.");
    load();
  };

  return (
    <div data-testid="admin-posts-tab">
      <div className="mb-8 flex items-center justify-between">
        <h2 className="font-serif text-3xl text-ink">Journal Posts</h2>
        <button
          data-testid="admin-post-new"
          onClick={() => setEditing({ title: "", tag: "Book Review", excerpt: "", body: "", cover_image: "" })}
          className="inline-flex items-center gap-2 rounded-full bg-plum px-6 py-2.5 text-sm font-semibold text-cream transition-transform duration-300 hover:scale-105"
        >
          <Plus size={15} /> New article
        </button>
      </div>

      {editing && <PostForm initial={editing} onSave={save} onCancel={() => setEditing(null)} />}

      <div className="space-y-3">
        {posts.map((p) => (
          <div key={p.id} data-testid={`admin-post-row-${p.slug}`} className="flex items-center justify-between gap-4 rounded-2xl border border-ink/10 bg-cream px-6 py-4">
            <div className="min-w-0">
              <p className="truncate font-serif text-lg text-ink">{p.title}</p>
              <p className="text-xs uppercase tracking-[0.15em] text-slate2">
                {p.tag} · {new Date(p.published_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
              </p>
            </div>
            <div className="flex shrink-0 gap-2">
              <button data-testid={`admin-post-edit-${p.slug}`} onClick={() => setEditing(p)} className="rounded-full border border-ink/20 p-2.5 text-ink transition-colors duration-200 hover:border-plum hover:text-plum" aria-label="Edit">
                <Pencil size={15} />
              </button>
              <button data-testid={`admin-post-delete-${p.slug}`} onClick={() => remove(p.id)} className="rounded-full border border-ink/20 p-2.5 text-ink transition-colors duration-200 hover:border-terracotta hover:text-terracotta" aria-label="Delete">
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        ))}
        {posts.length === 0 && <p className="font-serif text-lg italic text-slate2">No articles yet.</p>}
      </div>
    </div>
  );
}

function PostForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(form);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form data-testid="admin-post-form" onSubmit={submit} className="mb-10 rounded-3xl border border-plum/30 bg-sand/50 p-8">
      <div className="mb-6 flex items-center justify-between">
        <h3 className="font-serif text-2xl text-ink">{form.id ? "Edit article" : "New article"}</h3>
        <button type="button" data-testid="admin-post-cancel" onClick={onCancel} className="rounded-full border border-ink/20 p-2 text-ink hover:border-plum" aria-label="Cancel">
          <X size={15} />
        </button>
      </div>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Field label="Title">
            <input data-testid="admin-post-title" required minLength={2} value={form.title} onChange={set("title")} placeholder="Why picture books matter at every age" className={inputCls} />
          </Field>
        </div>
        <Field label="Tag">
          <input data-testid="admin-post-tag" value={form.tag} onChange={set("tag")} placeholder="Book Review" className={inputCls} />
        </Field>
        <Field label="Cover image URL (optional)">
          <input data-testid="admin-post-cover" value={form.cover_image} onChange={set("cover_image")} placeholder="/images/book-baskets.jpg" className={inputCls} />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Excerpt">
            <input data-testid="admin-post-excerpt" value={form.excerpt} onChange={set("excerpt")} placeholder="One or two sentences shown on the cards" className={inputCls} />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="Article body (blank line between paragraphs)">
            <textarea data-testid="admin-post-body" rows={10} value={form.body} onChange={set("body")} placeholder="Write the full review or note here…" className={inputCls} />
          </Field>
        </div>
      </div>
      <button type="submit" data-testid="admin-post-save" disabled={saving} className="mt-6 rounded-full bg-plum px-8 py-3 text-sm font-semibold text-cream transition-transform duration-300 hover:scale-105 disabled:opacity-60">
        {saving ? "Saving…" : form.id ? "Save changes" : "Publish article"}
      </button>
    </form>
  );
}

function EventsTab() {
  const [events, setEvents] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => apiFetch("/events").then((d) => d && setEvents(d));
  useEffect(() => {
    load();
  }, []);

  const save = async (form) => {
    if (form.id) {
      await apiFetch(`/events/${form.id}`, { method: "PUT", body: form });
      toast.success("Event updated.");
    } else {
      await apiFetch("/events", { method: "POST", body: form });
      toast.success("Event added to the calendar.");
    }
    setEditing(null);
    load();
  };

  const remove = async (id) => {
    await apiFetch(`/events/${id}`, { method: "DELETE" });
    toast.success("Event removed.");
    load();
  };

  return (
    <div data-testid="admin-events-tab">
      <div className="mb-8 flex items-center justify-between">
        <h2 className="font-serif text-3xl text-ink">Event Calendar</h2>
        <button
          data-testid="admin-event-new"
          onClick={() => setEditing({ title: "", date: "", time: "", location: "", description: "" })}
          className="inline-flex items-center gap-2 rounded-full bg-plum px-6 py-2.5 text-sm font-semibold text-cream transition-transform duration-300 hover:scale-105"
        >
          <Plus size={15} /> New event
        </button>
      </div>

      {editing && <EventForm initial={editing} onSave={save} onCancel={() => setEditing(null)} />}

      <div className="space-y-3">
        {events.map((e) => (
          <div key={e.id} data-testid={`admin-event-row-${e.id.slice(0, 8)}`} className="flex items-center justify-between gap-4 rounded-2xl border border-ink/10 bg-cream px-6 py-4">
            <div className="min-w-0">
              <p className="truncate font-serif text-lg text-ink">{e.title}</p>
              <p className="text-xs uppercase tracking-[0.15em] text-slate2">
                {e.date} {e.time ? `· ${e.time}` : ""} {e.location ? `· ${e.location}` : ""}
              </p>
            </div>
            <div className="flex shrink-0 gap-2">
              <button data-testid={`admin-event-edit-${e.id.slice(0, 8)}`} onClick={() => setEditing(e)} className="rounded-full border border-ink/20 p-2.5 text-ink transition-colors duration-200 hover:border-plum hover:text-plum" aria-label="Edit">
                <Pencil size={15} />
              </button>
              <button data-testid={`admin-event-delete-${e.id.slice(0, 8)}`} onClick={() => remove(e.id)} className="rounded-full border border-ink/20 p-2.5 text-ink transition-colors duration-200 hover:border-terracotta hover:text-terracotta" aria-label="Delete">
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        ))}
        {events.length === 0 && <p className="font-serif text-lg italic text-slate2">No events scheduled.</p>}
      </div>
    </div>
  );
}

function EventForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(form);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form data-testid="admin-event-form" onSubmit={submit} className="mb-10 rounded-3xl border border-plum/30 bg-sand/50 p-8">
      <div className="mb-6 flex items-center justify-between">
        <h3 className="font-serif text-2xl text-ink">{form.id ? "Edit event" : "New event"}</h3>
        <button type="button" data-testid="admin-event-cancel" onClick={onCancel} className="rounded-full border border-ink/20 p-2 text-ink hover:border-plum" aria-label="Cancel">
          <X size={15} />
        </button>
      </div>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Field label="Title">
            <input data-testid="admin-event-title" required minLength={2} value={form.title} onChange={set("title")} placeholder="Saturday Story Circle" className={inputCls} />
          </Field>
        </div>
        <Field label="Date">
          <input data-testid="admin-event-date" required type="date" value={form.date} onChange={set("date")} className={inputCls} />
        </Field>
        <Field label="Time">
          <input data-testid="admin-event-time" value={form.time} onChange={set("time")} placeholder="5:30 PM" className={inputCls} />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Location">
            <input data-testid="admin-event-location" value={form.location} onChange={set("location")} placeholder="Story Tree Corner" className={inputCls} />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="Description">
            <textarea data-testid="admin-event-description" rows={4} value={form.description} onChange={set("description")} placeholder="What should families expect?" className={inputCls} />
          </Field>
        </div>
      </div>
      <button type="submit" data-testid="admin-event-save" disabled={saving} className="mt-6 rounded-full bg-plum px-8 py-3 text-sm font-semibold text-cream transition-transform duration-300 hover:scale-105 disabled:opacity-60">
        {saving ? "Saving…" : form.id ? "Save changes" : "Add to calendar"}
      </button>
    </form>
  );
}

function VisitsTab() {
  const [visits, setVisits] = useState([]);

  useEffect(() => {
    apiFetch("/visits").then((d) => d && setVisits(d));
  }, []);

  return (
    <div data-testid="admin-visits-tab">
      <h2 className="mb-8 font-serif text-3xl text-ink">Visit Enquiries</h2>
      <div className="space-y-3">
        {visits.map((v) => (
          <div key={v.id} data-testid={`admin-visit-row-${v.id.slice(0, 8)}`} className="rounded-2xl border border-ink/10 bg-cream px-6 py-5">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="font-serif text-lg text-ink">{v.name}</p>
              <p className="text-xs uppercase tracking-[0.15em] text-plum">Prefers {v.preferred_date}</p>
            </div>
            <p className="mt-1 text-sm text-slate2">
              {v.email}
              {v.children_ages ? ` · children: ${v.children_ages}` : ""}
            </p>
            {v.message && <p className="mt-2 text-sm italic text-slate2">"{v.message}"</p>}
          </div>
        ))}
        {visits.length === 0 && <p className="font-serif text-lg italic text-slate2">No enquiries yet — they'll appear here and in your inbox.</p>}
      </div>
    </div>
  );
}
